import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { GTAOPass } from 'three/examples/jsm/postprocessing/GTAOPass';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass';
import { FXAAShader } from './shaders/FXAAShader.js';
import { SMAAShader } from './shaders/SMAAShader.js'
import { SMAAPass } from 'three/addons/postprocessing/SMAAPass.js';
import { RenderPixelatedPass } from 'three/addons/postprocessing/RenderPixelatedPass.js';
import { SSAARenderPass } from 'three/addons/postprocessing/SSAARenderPass.js';

/**
 * AO (Ambient Occlusion) 管理器
 * 基于GTAO算法的环境光遮蔽效果管理
 */
export class AOManager {
  constructor() {
    this.composer = null;
    this.renderPass = null;
    this.gtaoPass = null;
    this.fxaaPass = null;
    this.outputPass = null;
    this.enabled = true;
    this.initialized = false;
    
    // 抗锯齿设置
    this.antiAliasingType = 'FXAA'; // 'FXAA', 'SMAA', 'MSAA', 'None'
    this.fxaaEnabled = true;
    
    // GTAO参数
    this.aoParameters = {
      radius: 0.01,
      distanceExponent: 1.00,
      thickness: 0.01,
      scale: 2.00,
      samples: 32,
      distanceFallOff: 0.00,
      screenSpaceRadius: false,
    };
  }

  /**
   * 初始化GTAO效果和后处理管道
   * @param {THREE.WebGLRenderer} renderer - Three.js渲染器
   * @param {THREE.Scene} scene - Three.js场景
   * @param {THREE.Camera} camera - Three.js相机
   * @param {Object} options - 配置选项
   */
  init(renderer, scene, camera, options = {}) {
    if (this.initialized) {
      return;
    }

    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;

    // 合并用户配置
    if (options.aoParameters) {
      this.aoParameters = { ...this.aoParameters, ...options.aoParameters };
    }

    this.initPostProcessing(options);
    this.initialized = true;
  }

  /**
   * 初始化后处理管道
   * @param {Object} config - 配置对象
   */
  initPostProcessing(config) {
    const width = window.innerWidth;
    const height = window.innerHeight;
    // 创建效果合成器
    this.composer = new EffectComposer(this.renderer);
    
    // 启用多重采样抗锯齿(MSAA)
    this.composer.renderTarget1.samples = 8;
    this.composer.renderTarget2.samples = 8;
    
    // 基础渲染通道
    this.renderPass = new RenderPass(this.scene, this.camera);
    this.composer.addPass(this.renderPass);
    
    // GTAO通道
    this.gtaoPass = new GTAOPass(this.scene, this.camera, width, height);
    
    // 设置混合强度
    this.gtaoPass.blendIntensity = config.blendIntensity || 0.60;
    
    // 更新GTAO材质参数
    this.gtaoPass.updateGtaoMaterial(this.aoParameters);
    
    this.gtaoPass.enabled = this.enabled;
    this.composer.addPass(this.gtaoPass);
    this.composer.setSize(width, height);
    // 设置输出模式，默认为Default
    const outputMode = config.output || 'Default';
    this.updateOutput(outputMode);
    
    // FXAA抗锯齿通道（如果启用）
    // if (this.fxaaEnabled && config.antiAliasingType !== 'MSAA') {
    //   this.fxaaPass = new ShaderPass(SMAAShader);
    //   this.fxaaPass.material.uniforms['resolution'].value.x = 1 / width;
    //   this.fxaaPass.material.uniforms['resolution'].value.y = 1 / height;
    //   this.composer.addPass(this.fxaaPass);
    // }
    
    const smaaPass = new SMAAPass();
    this.composer.addPass( smaaPass );
    // 输出通道
    this.outputPass = new OutputPass();
    this.composer.addPass(this.outputPass);
    
    // 更新场景包围盒
    this.updateSceneClipBox();
  }

  /**
   * 更新场景包围盒
   */
  updateSceneClipBox() {
    if (this.gtaoPass && this.scene) {
      // 创建一个临时组，只包含需要参与AO计算的对象
      const tempGroup = new THREE.Group();
      
      this.scene.traverse((child) => {
        // 跳过不参与AO计算的对象（如地面）
        if (child.userData && (child.userData.ignoreAO || child.userData.excludeFromAO)) {
          return;
        }
        
        // 跳过名为shadowGround的对象
        if (child.name === 'shadowGround') {
          return;
        }
        
        // 只处理有几何体的网格对象
        if (child.isMesh && child.geometry) {
          tempGroup.add(child.clone());
        }
      });
      
      // 如果临时组有内容，使用它计算包围盒，否则使用整个场景
      if (tempGroup.children.length > 0) {
        const sceneBox = new THREE.Box3().setFromObject(tempGroup);
        this.gtaoPass.setSceneClipBox(sceneBox);
      } else {
        const sceneBox = new THREE.Box3().setFromObject(this.scene);
        this.gtaoPass.setSceneClipBox(sceneBox);
      }
    }
  }

  /**
   * 渲染场景（使用后处理）
   */
  render() {
    if (this.composer) {
      this.composer.render();
    }
  }

  /**
   * 启用/禁用AO效果
   * @param {boolean} enabled - 是否启用
   */
  setEnabled(enabled) {
    this.enabled = enabled;
    if (this.gtaoPass) {
      this.gtaoPass.enabled = enabled;
    }
  }

  /**
   * 获取AO效果启用状态
   * @returns {boolean}
   */
  getEnabled() {
    return this.enabled;
  }

  /**
   * 更新GTAO输出模式
   * @param {string} output - 输出模式
   */
  updateOutput(output) {
    if (!this.gtaoPass) return;
    
    // 使用数字值作为备用方案
    const outputMap = {
      'Default': 0,
      'Diffuse': 1,
      'AO Only': 2,
      'AO Only + Denoise': 3,
      'Depth': 4,
      'Normal': 5
    };
    
    if (this.gtaoPass.constructor.OUTPUT) {
      switch (output) {
        case 'Default':
          this.gtaoPass.output = this.gtaoPass.constructor.OUTPUT.Default || 0;
          break;
        case 'Diffuse':
          this.gtaoPass.output = this.gtaoPass.constructor.OUTPUT.Diffuse || 1;
          break;
        case 'AO Only':
          this.gtaoPass.output = this.gtaoPass.constructor.OUTPUT.AO || 2;
          break;
        case 'AO Only + Denoise':
          this.gtaoPass.output = this.gtaoPass.constructor.OUTPUT.Denoise || 3;
          break;
        case 'Depth':
          this.gtaoPass.output = this.gtaoPass.constructor.OUTPUT.Depth || 4;
          break;
        case 'Normal':
          this.gtaoPass.output = this.gtaoPass.constructor.OUTPUT.Normal || 5;
          break;
        default:
          this.gtaoPass.output = this.gtaoPass.constructor.OUTPUT.AO || 2;
      }
    } else {
      // 备用方案，使用数字值
      this.gtaoPass.output = outputMap[output] || 2;
    }
  }

  /**
   * 更新混合强度
   * @param {number} intensity - 混合强度
   */
  updateBlendIntensity(intensity) {
    if (this.gtaoPass) {
      this.gtaoPass.blendIntensity = intensity;
    }
  }

  /**
   * 更新GTAO参数
   * @param {Object} params - GTAO参数
   */
  updateGTAOParams(params) {
    if (!this.gtaoPass) return;
    
    // 更新内部参数
    this.aoParameters = { ...this.aoParameters, ...params };
    
    // 更新GTAO材质
    this.gtaoPass.updateGtaoMaterial(this.aoParameters);
  }

  /**
   * 设置抗锯齿类型
   * @param {string} type - 抗锯齿类型 ('FXAA', 'SMAA', 'MSAA', 'None')
   */
  setAntiAliasingType(type) {
    this.antiAliasingType = type;
    this.fxaaEnabled = (type === 'FXAA');
    
    // 重新初始化后处理管道
    if (this.initialized) {
      this.dispose();
      this.init(this.renderer, this.scene, this.camera, {
        antiAliasingType: type,
        output: this.getCurrentOutput(),
        blendIntensity: this.getBlendIntensity(),
        aoParameters: this.aoParameters
      });
    }
  }

  /**
   * 获取当前抗锯齿类型
   * @returns {string}
   */
  getAntiAliasingType() {
    return this.antiAliasingType;
  }

  /**
   * 处理窗口大小变化
   * @param {number} width - 新宽度
   * @param {number} height - 新高度
   */
  handleResize(width, height) {
    if (this.composer) {
      this.composer.setSize(width, height);
    }
    
    if (this.gtaoPass) {
      this.gtaoPass.setSize(width, height);
    }
    
    // 更新FXAA分辨率
    if (this.fxaaPass) {
      this.fxaaPass.material.uniforms['resolution'].value.x = 1 / width;
      this.fxaaPass.material.uniforms['resolution'].value.y = 1 / height;
    }
  }

  /**
   * 获取当前GTAO参数
   * @returns {Object}
   */
  getGTAOParams() {
    return { ...this.aoParameters };
  }

  /**
   * 获取混合强度
   * @returns {number}
   */
  getBlendIntensity() {
    return this.gtaoPass ? this.gtaoPass.blendIntensity : 0.60;
  }

  /**
   * 获取当前输出模式
   * @returns {string}
   */
  getCurrentOutput() {
    if (!this.gtaoPass) return 'Default';
    
    const outputValue = this.gtaoPass.output;
    const outputMap = {
      0: 'Default',
      1: 'Diffuse', 
      2: 'AO Only',
      3: 'AO Only + Denoise',
      4: 'Depth',
      5: 'Normal'
    };
    
    return outputMap[outputValue] || 'Default';
  }

  /**
   * 获取预设配置
   * @param {string} preset - 预设名称
   * @returns {Object} 配置对象
   */
  static getPreset(preset) {
    const presets = {
      'high': {
        blendIntensity: 0.60,
        output: 'Default',
        aoParameters: {
          radius: 0.01,
          distanceExponent: 1.00,
          thickness: 0.01,
          scale: 2.00,
          samples: 32,
          distanceFallOff: 0.00,
          screenSpaceRadius: false,
        }
      },
      'medium': {
        blendIntensity: 0.3,
        output: 'AO Only',
        aoParameters: {
          radius: 0.25,
          distanceExponent: 1.0,
          thickness: 1.0,
          scale: 1.0,
          samples: 8,
          distanceFallOff: 1.0,
          screenSpaceRadius: false,
        }
      },
      'low': {
        blendIntensity: 0.64,
        output: 'Default',
        aoParameters: {
          radius: 1.00,
          distanceExponent: 1.00,
          thickness: 10.00,
          scale: 2.00,
          samples: 32,
          distanceFallOff: 1.00,
          screenSpaceRadius: false,
        }
      }
    };

    return presets[preset] || presets.high;
  }

  /**
   * 销毁AOManager
   */
  dispose() {
    if (this.composer) {
      this.composer.dispose();
    }
    
    if (this.fxaaPass) {
      this.fxaaPass.dispose();
    }
    
    this.composer = null;
    this.renderPass = null;
    this.gtaoPass = null;
    this.fxaaPass = null;
    this.outputPass = null;
    this.initialized = false;
  }
}

export default AOManager;